import { Box, Button, Grid, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PromotionView } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { enumParam, intParam, useUrlState } from '@/components/useUrlState';
import { useCanWrite } from '@/auth/authHooks';
import { useTenantLink } from '@/tenant/useTenant';
import { PromotionCostCard } from './PromotionCostCard';
import { PromotionTable } from './PromotionTable';
import { RedemptionsDrawer } from './RedemptionsDrawer';
import { usePromotions } from './usePromotions';

function defaultSelected(promotions: PromotionView[]): number | undefined {
  return promotions.find((promo) => promo.status === 'ACTIVE')?.id ?? promotions[0]?.id;
}

/**
 * The selection in the URL (TMM-B6): `?promotion=<id>` (pushed, so Back returns to the one before;
 * the current selection is a no-op) and `?redemptions=1` while its drawer is open. Close goes back
 * when that push is still the previous entry, and replaces otherwise (TMMRSL-0074).
 */
const SCHEMA = { promotion: intParam(0), redemptions: enumParam(['', '1'], '') };

export function PromotionsScreen() {
  const { t } = useTranslation();
  const canWrite = useCanWrite();
  const link = useTenantLink();
  const { status, data, error, reload, setStatus } = usePromotions();
  const url = useUrlState(SCHEMA);
  const pickedId = url.values.promotion || undefined;

  const promotions = data?.promotions.promotions ?? [];
  const selectedId = promotions.some((promo) => promo.id === pickedId)
    ? pickedId
    : defaultSelected(promotions);
  const selected = promotions.find((promo) => promo.id === selectedId);
  const drawer = url.values.redemptions === '1' && selected ? selected : null;
  const pick = (id: number) => {
    if (id === selectedId) return;
    url.set({ promotion: id, redemptions: '' }, { push: true });
  };

  return (
    <Box>
      <PageHeader
        id="promotions"
        actions={
          canWrite ? (
            <Button variant="contained" component={RouterLink} to={link('promotions/new')}>
              {t('promotions.new')}
            </Button>
          ) : undefined
        }
      />
      {status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          </Grid>
        </Box>
      ) : null}
      {status === 'error' ? <ApiErrorState what={t('errors.what.promotions')} error={error} onRetry={reload} /> : null}
      {status === 'ready' && data ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <PromotionTable
              promotions={promotions}
              selectedId={selectedId}
              busy={false}
              openSearch={(id) => url.searchWith({ promotion: id, redemptions: '' })}
              openState={url.linkState}
              onSetStatus={async (id, next) => {
                await setStatus(id, next);
              }}
              onRedemptions={(promo) => {
                if (promo.id === undefined) return;
                if (url.values.redemptions === '1' && promo.id === selectedId) return;
                url.set({ promotion: promo.id, redemptions: '1' }, { push: true });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <PromotionCostCard
              promotions={promotions}
              selected={selected}
              tenantCard={data.tenantCard}
              onSelect={pick}
            />
          </Grid>
        </Grid>
      ) : null}
      <RedemptionsDrawer
        open={drawer !== null}
        promotion={drawer}
        onClose={() => url.dismiss('redemptions')}
      />
    </Box>
  );
}
